"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSession } from "next-auth/react";
import Pusher from "pusher-js";

const MessengerNotificationsContext = createContext({
    unreadCount: 0,
    unreadDiscussionIds: new Set(),
    subscribe: () => () => {},
    markRead: () => {},
    markUnread: () => {},
    setActiveDiscussion: () => {},
    isDiscussionActive: () => false,
});

// Un identifiant de discussion arrive par quatre chemins : le compteur initial,
// Pusher, un onglet voisin et le fil ouvert à l'écran. Rien ne garantit qu'ils
// portent partout le même type, et un Set qui mélange 42 et "42" ne se vide
// jamais : `has` ne convertit pas. Tout passe donc par ce filtre avant d'entrer
// dans l'ensemble ou d'en sortir.
const toDiscussionId = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const id = Number(value);
    return Number.isFinite(id) ? id : null;
};

// Une lecture décidée ici met un instant à devenir vraie côté serveur. Pendant
// cette fenêtre, une resynchronisation rallumerait la pastille qu'on vient
// d'éteindre : on ignore ce que le serveur dit d'une discussion lue de nos
// propres mains il y a moins de quinze secondes.
const RECENT_READ_GRACE_MS = 15000;

// Basculer entre deux onglets ne doit pas déclencher une rafale de requêtes :
// le compteur fait scanner cinq cents discussions au serveur, et la
// synchronisation n'est qu'un filet — le chemin normal, entre onglets, est
// immédiat.
const RESYNC_THROTTLE_MS = 30000;

export function useMessengerNotifications() {
    return useContext(MessengerNotificationsContext);
}

export function MessengerNotificationsProvider({ children }) {
    const { data: session, status } = useSession();
    const userId = session?.user?.userId;

    const [unreadDiscussionIds, setUnreadDiscussionIds] = useState(
        () => new Set(),
    );

    const listenersRef = useRef(new Map());
    const activeDiscussionIdRef = useRef(null);
    const broadcastRef = useRef(null);
    const recentlyReadRef = useRef(new Map());
    const lastSyncAtRef = useRef(0);
    const markingReadRef = useRef(new Set());

    const setActiveDiscussion = useCallback((discussionId) => {
        activeDiscussionIdRef.current = toDiscussionId(discussionId);
    }, []);

    // Le fil ouvert à l'écran en ce moment. Un abonné qui veut notifier a
    // besoin de le savoir : prévenir quelqu'un d'un message qu'il est en train
    // de lire est la façon la plus sûre de lui faire couper les notifications.
    const isDiscussionActive = useCallback((discussionId) => {
        const id = toDiscussionId(discussionId);
        return id !== null && activeDiscussionIdRef.current === id;
    }, []);

    const subscribe = useCallback((eventName, callback) => {
        if (!listenersRef.current.has(eventName)) {
            listenersRef.current.set(eventName, new Set());
        }
        const set = listenersRef.current.get(eventName);
        set.add(callback);
        return () => {
            set.delete(callback);
        };
    }, []);

    const emit = useCallback((eventName, data) => {
        const set = listenersRef.current.get(eventName);
        if (!set) return;
        set.forEach((cb) => {
            try {
                cb(data);
            } catch (e) {
                console.error(`Messenger listener error (${eventName}):`, e);
            }
        });
    }, []);

    // Poser l'état, sans rien dire à personne. C'est ce que font les événements
    // REÇUS, qu'ils viennent de Pusher ou d'un onglet voisin : les rediffuser
    // ferait tourner l'information en rond entre les onglets.
    const applyRead = useCallback((discussionId) => {
        const id = toDiscussionId(discussionId);
        if (id === null) return;
        recentlyReadRef.current.set(id, Date.now());
        setUnreadDiscussionIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const applyUnread = useCallback((discussionId) => {
        const id = toDiscussionId(discussionId);
        if (id === null) return;
        recentlyReadRef.current.delete(id);
        setUnreadDiscussionIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const postToOtherTabs = useCallback((type, data) => {
        const channel = broadcastRef.current;
        if (!channel) return;
        try {
            channel.postMessage({ type, data });
        } catch (e) {
            console.error("BroadcastChannel post failed:", e);
        }
    }, []);

    // Ce que cet onglet DÉCIDE, lui. Une lecture est une information purement
    // locale : les autres onglets ne peuvent pas la deviner. Elle transitait
    // jusqu'ici par le serveur puis par Pusher, ce qui la rendait tributaire
    // d'un aller-retour réseau ET de l'onglet élu pour tenir la connexion
    // temps réel — un onglet endormi, une connexion coupée, et la pastille
    // restait allumée partout ailleurs. Elle part maintenant directement aux
    // onglets voisins, sans passer par le réseau.
    const markRead = useCallback(
        (discussionId) => {
            const id = toDiscussionId(discussionId);
            if (id === null) return;
            applyRead(id);
            postToOtherTabs("messengerDiscussionReadChanged", {
                discussionId: id,
                result: "read",
            });
        },
        [applyRead, postToOtherTabs],
    );

    const markUnread = useCallback(
        (discussionId) => {
            const id = toDiscussionId(discussionId);
            if (id === null) return;
            applyUnread(id);
            postToOtherTabs("messengerDiscussionReadChanged", {
                discussionId: id,
                result: "unread",
            });
        },
        [applyUnread, postToOtherTabs],
    );

    // Un message arrive sur le fil ouvert ICI. On le tient pour lu, comme
    // avant, mais cette décision doit sortir de l'onglet : les autres gardaient
    // leur pastille, et le serveur — qui efface tous les « vu » à chaque envoi
    // — la faisait revenir au prochain chargement. La route est celle de
    // l'ouverture d'un fil : elle enregistre la lecture et allume le « vu »
    // chez l'auteur, qui ne le voyait pas non plus dans ce cas.
    const markReadWhileOpen = useCallback(
        async (discussionId) => {
            const id = toDiscussionId(discussionId);
            if (id === null || markingReadRef.current.has(id)) return;
            markingReadRef.current.add(id);
            markRead(id);
            try {
                await fetch("/api/messenger/get/answers", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ messageId: id }),
                });
            } catch (e) {
                console.error("Failed to mark open discussion as read", e);
            } finally {
                markingReadRef.current.delete(id);
            }
        },
        [markRead],
    );

    const handleIncomingEvent = useCallback(
        (type, data) => {
            if (type === "messengerNewMessage") {
                if (Number(data.authorId) === Number(userId)) return;
                if (isDiscussionActive(data.discussionId)) {
                    markReadWhileOpen(data.discussionId);
                } else {
                    applyUnread(data.discussionId);
                }
                emit("messengerNewMessage", data);
            } else if (type === "messengerDiscussionReadChanged") {
                if (data.result === "read") {
                    applyRead(data.discussionId);
                } else {
                    applyUnread(data.discussionId);
                }
                emit("messengerDiscussionReadChanged", data);
            }
        },
        [
            userId,
            isDiscussionActive,
            markReadWhileOpen,
            applyRead,
            applyUnread,
            emit,
        ],
    );

    // La vérité du serveur, relue au chargement puis chaque fois que l'onglet
    // revient au premier plan. C'est le filet : un événement manqué pendant que
    // l'onglet dormait se rattrape tout seul au retour, sans rechargement.
    const syncFromServer = useCallback(async () => {
        const now = Date.now();
        if (now - lastSyncAtRef.current < RESYNC_THROTTLE_MS) return;
        lastSyncAtRef.current = now;

        try {
            const res = await fetch("/api/messenger/get/unread-count", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) return;
            const data = await res.json();

            for (const [id, readAt] of recentlyReadRef.current) {
                if (Date.now() - readAt >= RECENT_READ_GRACE_MS) {
                    recentlyReadRef.current.delete(id);
                }
            }

            const fresh = new Set();
            for (const raw of data.discussionIds || []) {
                const id = toDiscussionId(raw);
                if (id === null) continue;
                // Le fil sous les yeux ne se rallume pas.
                if (activeDiscussionIdRef.current === id) continue;
                if (recentlyReadRef.current.has(id)) continue;
                fresh.add(id);
            }

            setUnreadDiscussionIds((prev) => {
                if (
                    prev.size === fresh.size &&
                    [...fresh].every((id) => prev.has(id))
                ) {
                    return prev;
                }
                return fresh;
            });
        } catch (e) {
            console.error("Failed to sync messenger unread count", e);
        }
    }, []);

    useEffect(() => {
        if (status !== "authenticated" || !userId) return;

        lastSyncAtRef.current = 0;
        (async () => {
            await syncFromServer();
        })();

        const resyncIfVisible = () => {
            if (document.hidden) return;
            syncFromServer();
        };

        document.addEventListener("visibilitychange", resyncIfVisible);
        window.addEventListener("focus", resyncIfVisible);
        // Une coupure réseau fait manquer les événements temps réel sans que
        // rien ne le signale : le retour en ligne vaut relecture.
        window.addEventListener("online", resyncIfVisible);
        return () => {
            document.removeEventListener("visibilitychange", resyncIfVisible);
            window.removeEventListener("focus", resyncIfVisible);
            window.removeEventListener("online", resyncIfVisible);
        };
    }, [status, userId, syncFromServer]);

    // Le canal entre onglets vit désormais à part de Pusher. Il ne portait
    // jusqu'ici que les retransmissions de l'onglet élu, si bien qu'il ne
    // servait à rien tant que la connexion temps réel n'était pas au
    // rendez-vous ; il transporte maintenant aussi ce que chaque onglet décide
    // de son côté, ce qui ne demande ni serveur ni élection.
    useEffect(() => {
        if (status !== "authenticated" || !userId) return;
        if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
            return;
        }

        const channel = new BroadcastChannel(`messenger-events-${userId}`);
        channel.onmessage = (e) => {
            if (!e.data || !e.data.type) return;
            handleIncomingEvent(e.data.type, e.data.data);
        };
        broadcastRef.current = channel;

        return () => {
            broadcastRef.current = null;
            channel.close();
        };
    }, [status, userId, handleIncomingEvent]);

    // Connexion Pusher + leader election multi-onglets.
    // Pour économiser les connexions Pusher quand un utilisateur a plusieurs
    // onglets ouverts, on élit un onglet "leader" via la Web Locks API. Lui
    // seul maintient la connexion Pusher et rediffuse les events aux autres
    // onglets ("followers") via BroadcastChannel. Si l'onglet leader se
    // ferme, le lock est libéré et un follower devient automatiquement
    // leader. Si la Web Locks API n'est pas dispo (vieux Safari, etc.),
    // chaque onglet ouvre sa propre connexion (comportement initial).
    useEffect(() => {
        if (status !== "authenticated" || !userId) return;
        if (!process.env.PUSHER_APP_KEY || !process.env.PUSHER_APP_CLUSTER) {
            return;
        }

        let pusher = null;
        let channel = null;
        let releaseLock = null;
        let unmounted = false;
        const lockAbortController =
            typeof AbortController !== "undefined"
                ? new AbortController()
                : null;

        const relay = (type, data) => {
            const broadcastChannel = broadcastRef.current;
            if (!broadcastChannel) return;
            try {
                broadcastChannel.postMessage({ type, data });
            } catch (e) {
                console.error("BroadcastChannel post failed:", e);
            }
        };

        const startPusherAsLeader = () => {
            if (unmounted) return;
            pusher = new Pusher(process.env.PUSHER_APP_KEY, {
                cluster: process.env.PUSHER_APP_CLUSTER,
                authEndpoint: "/api/pusherauth",
            });
            channel = pusher.subscribe(`private-user-${userId}`);

            ["messengerNewMessage", "messengerDiscussionReadChanged"].forEach(
                (eventName) => {
                    channel.bind(eventName, (data) => {
                        handleIncomingEvent(eventName, data);
                        relay(eventName, data);
                    });
                },
            );
        };

        const supportsBroadcastChannel =
            typeof window !== "undefined" && "BroadcastChannel" in window;
        const supportsWebLocks =
            typeof window !== "undefined" &&
            typeof window.navigator !== "undefined" &&
            window.navigator.locks &&
            typeof window.navigator.locks.request === "function";

        if (supportsWebLocks && supportsBroadcastChannel) {
            const lockOptions = lockAbortController
                ? { signal: lockAbortController.signal }
                : {};
            window.navigator.locks
                .request(
                    `messenger-pusher-leader-${userId}`,
                    lockOptions,
                    () =>
                        new Promise((resolve) => {
                            if (unmounted) {
                                resolve();
                                return;
                            }
                            startPusherAsLeader();
                            releaseLock = resolve;
                        }),
                )
                .catch((err) => {
                    if (err && err.name !== "AbortError") {
                        console.error(
                            "[MessengerNotifications] lock acquisition failed",
                            err,
                        );
                    }
                });
        } else {
            // Fallback : pas de Web Locks (ou pas de BroadcastChannel), on
            // ouvre une connexion Pusher par onglet comme avant.
            startPusherAsLeader();
        }

        return () => {
            unmounted = true;
            if (channel) {
                channel.unbind_all();
                channel = null;
            }
            if (pusher) {
                pusher.disconnect();
                pusher = null;
            }
            if (releaseLock) {
                releaseLock();
                releaseLock = null;
            } else if (lockAbortController) {
                lockAbortController.abort();
            }
        };
    }, [status, userId, handleIncomingEvent]);

    const value = useMemo(
        () => ({
            unreadCount: unreadDiscussionIds.size,
            unreadDiscussionIds,
            subscribe,
            markRead,
            markUnread,
            setActiveDiscussion,
            isDiscussionActive,
        }),
        [
            unreadDiscussionIds,
            subscribe,
            markRead,
            markUnread,
            setActiveDiscussion,
            isDiscussionActive,
        ],
    );

    return (
        <MessengerNotificationsContext.Provider value={value}>
            {children}
        </MessengerNotificationsContext.Provider>
    );
}
